import { AHThemeType, useTheme } from "./AHTheme/AHTheme";
import React, { useMemo } from "react";
import { useFragment, graphql } from "react-relay";
import { AHStatusCircle } from "./AHStatusCircle";
import Color from "color";
import { DateTime } from "luxon";
import { CircleSize, useCircleSize } from "./LayoutProvider";
import { useTime } from "./useTime";
import {
  RoutineOccasionStatusCircle_RoutineOccasion$key,
  RoutineOccasionStatus,
} from "./__generated__/RoutineOccasionStatusCircle_RoutineOccasion.graphql";
import { RoutineOccasionStatusCircleInline_RoutineOccasion$key } from "./__generated__/RoutineOccasionStatusCircleInline_RoutineOccasion.graphql";

const calculateProgressFromDates = (
  now: DateTime,
  to?: string | null,
  from?: string | null
) => {
  if (!to || !from) {
    return 0;
  }
  const totalSecondsPending = Math.abs(
    DateTime.fromISO(to).diff(DateTime.fromISO(from)).as("seconds")
  );

  const secondsSinceStarted = Math.abs(
    now.diff(DateTime.fromISO(from)).as("seconds")
  );

  return Math.min(secondsSinceStarted / totalSecondsPending, 1);
};

export const colorForStatus = (
  theme: AHThemeType,
  status?: RoutineOccasionStatus
): string => {
  switch (status) {
    case "IN_PROGRESS":
      return theme.primary;
    case "REQUIRED_COMPLETED":
    case "COMPLETED":
      return theme.positive;
    case "IN_PROGRESS_EXPIRED":
    case "NOT_COMPLETED":
      return theme.negative;
    case "NO_TASKS_COMPLETED":
    case "FUTURE":
      return theme.neutral;
    case "NOT_COMPLETED_STARTED":
      return theme.semiNegative;
    case undefined:
      return theme.neutral;
  }
  //    throw new Error(status + "has no color");
};

export const fillForStatus = (status?: RoutineOccasionStatus) => {
  switch (status) {
    case "COMPLETED":
    case "NOT_COMPLETED":
      return true;
    case "NO_TASKS_COMPLETED":
    case "REQUIRED_COMPLETED":
    case "IN_PROGRESS":
    case "IN_PROGRESS_EXPIRED":
    case "FUTURE":
    case undefined:
      return false;
  }
};

const iconForStatus = (status?: RoutineOccasionStatus) => {
  switch (status) {
    case "IN_PROGRESS":
    case "IN_PROGRESS_EXPIRED":
    case "FUTURE":
    case "NO_TASKS_COMPLETED":
    case "REQUIRED_COMPLETED":
      return "arrow-right";
    case "COMPLETED":
      return "check";
    case "NOT_COMPLETED":
      return "clear";
    case undefined:
      return undefined;
  }
};

const RoutineOccasionStatusCircle = (props: {
  status?: RoutineOccasionStatus;
  expireAt?: string | null;
  startAt?: string | null;
  size?: number;
  circleSize?: CircleSize;
}) => {
  const theme = useTheme();
  const now = useTime(6000);
  const size = useCircleSize(props.circleSize ?? "medium");
  const status = props.status;
  const isFilled = useMemo(() => fillForStatus(status), [status]);

  const mainColor = colorForStatus(theme, status);
  const trackBackgroundColor = useMemo(() => {
    if (status === "FUTURE") {
      return mainColor;
    }
    if (status === "REQUIRED_COMPLETED") {
      return Color(mainColor).mix(Color(theme.surface), 0.5).toString();
    } else {
      return Color(theme.primary).mix(Color(theme.surface), 0.6).toString();
    }
  }, [mainColor, status, theme.primary, theme.surface]);

  const progress = useMemo(
    () =>
      status === "FUTURE"
        ? 0
        : (calculateProgressFromDates(now, props.expireAt, props.startAt) ?? 0),
    [now, props.expireAt, props.startAt, status]
  );
  const icon = useMemo(() => iconForStatus(status), [status]);

  return (
    <AHStatusCircle
      testID="RoutineOccasionStatusCircle"
      filledColor={mainColor}
      shouldInsetFill={false}
      icon={icon}
      iconColor={isFilled ? theme.lightText : mainColor}
      progress={progress}
      trackIsFilled={progress === 1 || status === "REQUIRED_COMPLETED"}
      trackFillColor={mainColor}
      isFilled={isFilled ?? false}
      size={size}
      trackBackgroundColor={trackBackgroundColor}
    />
  );
};

export const colorForStatusRelay = (
  theme: AHThemeType,
  status?: RoutineOccasionStatus
): string => {
  switch (status) {
    case "IN_PROGRESS":
      return theme.primary;
    case "REQUIRED_COMPLETED":
    case "COMPLETED":
      return theme.positive;
    case "IN_PROGRESS_EXPIRED":
    case "NOT_COMPLETED":
      return theme.negative;
    case "NOT_COMPLETED_STARTED":
      return theme.semiNegative;
    case "NO_TASKS_COMPLETED":
    case "FUTURE":
      return theme.neutral;
    case undefined:
      return theme.neutral;
  }
  //    throw new Error(status + "has no color");
};

export const fillForStatusRelay = (status?: RoutineOccasionStatus) => {
  switch (status) {
    case "COMPLETED":
    case "NOT_COMPLETED":
    case "NOT_COMPLETED_STARTED":
      return true;
    case "REQUIRED_COMPLETED":
    case "IN_PROGRESS":
    case "IN_PROGRESS_EXPIRED":
    case "FUTURE":
    case undefined:
      return false;
  }
};

const iconForStatusRelay = (status?: RoutineOccasionStatus) => {
  switch (status) {
    case "IN_PROGRESS":
    case "IN_PROGRESS_EXPIRED":
    case "FUTURE":
    case "REQUIRED_COMPLETED":
      return "arrow-right";
    case "COMPLETED":
      return "check";
    case "NOT_COMPLETED":
    case "NOT_COMPLETED_STARTED":
      return "clear";
    case undefined:
      return undefined;
  }
};

export const RoutineOccasionStatusCircleRelay = (props: {
  occasion: RoutineOccasionStatusCircle_RoutineOccasion$key;
  size?: number;
  circleSize?: CircleSize;
}) => {
  const { status, startAt, expireAt, deadlineAt } = useFragment(
    graphql`
      fragment RoutineOccasionStatusCircle_RoutineOccasion on RoutineOccasion {
        status(calculateNoTasks: true, calculateNotCompletedStarted: true)
        expireAt
        deadlineAt
        startAt
      }
    `,
    props.occasion
  );
  const theme = useTheme();
  const now = useTime(6000);
  const size = useCircleSize(props.circleSize ?? "medium");

  const isFilled = useMemo(() => fillForStatusRelay(status), [status]);

  const mainColor = colorForStatusRelay(theme, status);
  const trackBackgroundColor = useMemo(() => {
    if (status === "FUTURE") {
      return mainColor;
    }
    if (status === "REQUIRED_COMPLETED") {
      return Color(mainColor).mix(Color(theme.surface), 0.5).toString();
    } else {
      return Color(theme.primary).mix(Color(theme.surface), 0.6).toString();
    }
  }, [mainColor, status, theme.primary, theme.surface]);

  const progress = useMemo(
    () =>
      status === "FUTURE"
        ? 0
        : (calculateProgressFromDates(now, deadlineAt ?? expireAt, startAt) ??
          0),
    [status, now, deadlineAt, expireAt, startAt]
  );
  const icon = useMemo(() => iconForStatusRelay(status), [status]);

  return (
    <AHStatusCircle
      testID="RoutineOccasionStatusCircle"
      filledColor={mainColor}
      shouldInsetFill={false}
      icon={icon}
      iconColor={isFilled ? theme.lightText : mainColor}
      progress={progress}
      trackIsFilled={progress === 1 || status === "REQUIRED_COMPLETED"}
      trackFillColor={mainColor}
      isFilled={isFilled ?? false}
      size={size}
      trackBackgroundColor={trackBackgroundColor}
    />
  );
};

const inlineIconForStatusRelay = (status?: RoutineOccasionStatus) => {
  switch (status) {
    case "IN_PROGRESS":
    case "IN_PROGRESS_EXPIRED":
    case "FUTURE":
    case "REQUIRED_COMPLETED":
    case "NOT_COMPLETED":
    case "NOT_COMPLETED_STARTED":
      return undefined;
    case "COMPLETED":
      return "check";
    case undefined:
      return undefined;
  }
};

export const RoutineOccasionInlineStatusCircleRelay = (props: {
  occasion: RoutineOccasionStatusCircleInline_RoutineOccasion$key;
  size?: number;
  circleSize?: CircleSize;
}) => {
  const { status, startAt, deadlineAt } = useFragment(
    graphql`
      fragment RoutineOccasionStatusCircleInline_RoutineOccasion on RoutineOccasion {
        id
        status(calculateNoTasks: true, calculateNotCompletedStarted: true)
        deadlineAt
        startAt
      }
    `,
    props.occasion
  );

  const theme = useTheme();
  const now = useTime(6000);
  const size = useCircleSize(props.circleSize ?? "medium");

  const isFilled = status === "COMPLETED";

  const mainColor = colorForStatusRelay(theme, status);
  const trackBackgroundColor = useMemo(() => {
    if (status === "FUTURE") {
      return mainColor;
    }
    if (status === "IN_PROGRESS_EXPIRED" || status === "NOT_COMPLETED") {
      return theme.negative;
    }
    if (status === "REQUIRED_COMPLETED") {
      return Color(mainColor).mix(Color(theme.surface), 0.5).toString();
    } else {
      return Color(theme.primary).mix(Color(theme.surface), 0.6).toString();
    }
  }, [mainColor, status, theme.negative, theme.primary, theme.surface]);

  const progress = useMemo(
    () =>
      status === "FUTURE"
        ? 0
        : (calculateProgressFromDates(now, deadlineAt, startAt) ?? 0),
    [status, now, deadlineAt, startAt]
  );
  const icon = useMemo(() => inlineIconForStatusRelay(status), [status]);

  return (
    <AHStatusCircle
      testID="RoutineOccasionInlineStatusCircle"
      filledColor={mainColor}
      shouldInsetFill={false}
      icon={icon}
      iconColor={isFilled ? theme.lightText : mainColor}
      progress={progress}
      trackIsFilled={
        deadlineAt === null || progress === 1 || status === "REQUIRED_COMPLETED"
      }
      trackFillColor={mainColor}
      isFilled={isFilled}
      size={size}
      trackBackgroundColor={trackBackgroundColor}
    />
  );
};
